import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getThumbnails,
  generateThumbnail,
  getThumbnailUrl,
  ThumbnailsInfo,
} from "../services/mediaService";
import { ScriptResponse } from "../types";

const SMALL_BTN =
  "px-3 py-1 rounded-md text-xs transition whitespace-nowrap disabled:opacity-50";

// Optional: turns each thumbnail idea into a picture (PRO and Agency). The text ideas always stay visible above.
const ThumbnailsPanel = ({ script }: { script: ScriptResponse }) => {
  const navigate = useNavigate();
  const isShort = script.scriptFormat === "SHORTS";

  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [info, setInfo] = useState<ThumbnailsInfo | null>(null);
  const [imageUrls, setImageUrls] = useState<Record<number, string>>({});
  const [generating, setGenerating] = useState<number | null>(null);
  const [error, setError] = useState("");

  // Load the ideas the first time the section is opened
  useEffect(() => {
    if (!open || loaded) return;
    const load = async () => {
      try {
        const data = await getThumbnails(script.id);
        setInfo(data);
        const urls: Record<number, string> = {};
        for (const t of data.ideas) {
          if (t.hasImage)
            urls[t.ideaNumber] = await getThumbnailUrl(script.id, t.ideaNumber);
        }
        setImageUrls(urls);
      } catch (err: any) {
        setError(
          err.response?.data?.message ||
            "Could not load the thumbnails. Please try again.",
        );
      } finally {
        setLoaded(true);
      }
    };
    load();
  }, [open, loaded, script.id]);

  // Free the pictures from memory when leaving the page
  useEffect(() => {
    return () => {
      Object.values(imageUrls).forEach((u) => window.URL.revokeObjectURL(u));
    };
  }, [imageUrls]);

  const handleGenerate = async (ideaNumber: number) => {
    setError("");
    setGenerating(ideaNumber);
    try {
      await generateThumbnail(script.id, ideaNumber);
      const url = await getThumbnailUrl(script.id, ideaNumber);
      setImageUrls((prev) => {
        if (prev[ideaNumber]) window.URL.revokeObjectURL(prev[ideaNumber]);
        return { ...prev, [ideaNumber]: url };
      });
      setInfo(await getThumbnails(script.id)); // refresh the weekly usage numbers
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Could not create the thumbnail. Please try again.",
      );
    } finally {
      setGenerating(null);
    }
  };

  const handleDownload = (ideaNumber: number) => {
    const url = imageUrls[ideaNumber];
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = `${(script.title || "thumbnail").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-thumbnail-${ideaNumber}.png`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const limitReached =
    !!info && info.canGenerate && info.usedThisWeek >= info.weeklyLimit;
  const unlimited = !!info && info.weeklyLimit > 1000;

  return (
    <div className="bg-gray-900 rounded-xl p-4 border border-gray-700">
      {/* Header: click to open or close */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <div>
          <h3 className="text-gray-400 text-xs font-semibold uppercase">
            Thumbnail pictures{" "}
            <span className="normal-case font-normal text-gray-500">
              (optional)
            </span>
          </h3>
          <p className="text-gray-500 text-xs mt-1">
            Turn the thumbnail ideas into ready-to-use pictures, then pick your
            favourite.
          </p>
        </div>
        <span className="text-gray-400 text-sm">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          {!loaded ? (
            <p className="text-gray-400 text-sm">Loading...</p>
          ) : (
            <>
              {info && info.canGenerate && (
                <p className="text-gray-400 text-xs">
                  {unlimited
                    ? "Unlimited images (admin)."
                    : `${info.usedThisWeek} of ${info.weeklyLimit} images used this week (shared with reference images). Resets on ${new Date(info.resetsOn).toLocaleDateString()}.`}
                </p>
              )}

              {/* Free plan: the text ideas are free; pictures need PRO or Agency */}
              {info && !info.canGenerate && (
                <div className="bg-gray-800 border border-yellow-600 rounded-lg p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <p className="text-yellow-400 text-xs">
                    Use the thumbnail ideas above to design your own, or upgrade
                    to PRO or Agency to generate the pictures here.
                  </p>
                  <button
                    onClick={() => navigate("/pricing")}
                    className={`${SMALL_BTN} bg-yellow-500 hover:bg-yellow-400 text-black font-semibold`}
                  >
                    Upgrade
                  </button>
                </div>
              )}

              {error && (
                <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* The ideas side by side, each with its picture */}
              <div
                className={`grid gap-3 ${isShort ? "grid-cols-3" : "grid-cols-1 sm:grid-cols-3"}`}
              >
                {info?.ideas.map((t) => (
                  <div
                    key={t.ideaNumber}
                    className="bg-gray-800 rounded-lg p-3 border border-gray-700 flex flex-col"
                  >
                    <span className="text-purple-400 font-semibold text-xs mb-2">
                      Idea {t.ideaNumber}
                    </span>

                    {imageUrls[t.ideaNumber] ? (
                      <img
                        src={imageUrls[t.ideaNumber]}
                        alt={`Thumbnail ${t.ideaNumber}`}
                        className={`w-full rounded-md border border-gray-600 object-cover ${isShort ? "aspect-[9/16]" : "aspect-video"}`}
                      />
                    ) : (
                      <div
                        className={`w-full rounded-md border border-dashed border-gray-600 flex items-center justify-center text-gray-500 text-xs p-2 text-center ${isShort ? "aspect-[9/16]" : "aspect-video"}`}
                      >
                        {t.idea}
                      </div>
                    )}

                    <div className="flex gap-2 mt-2">
                      {info.canGenerate && (
                        <button
                          onClick={() => handleGenerate(t.ideaNumber)}
                          disabled={generating !== null || limitReached}
                          className={`${SMALL_BTN} flex-1 bg-purple-600 hover:bg-purple-700 text-white`}
                        >
                          {generating === t.ideaNumber
                            ? "Creating..."
                            : imageUrls[t.ideaNumber]
                              ? "Regenerate"
                              : "Generate"}
                        </button>
                      )}
                      {imageUrls[t.ideaNumber] && (
                        <button
                          onClick={() => handleDownload(t.ideaNumber)}
                          className={`${SMALL_BTN} flex-1 bg-gray-700 hover:bg-gray-600 text-white`}
                        >
                          Download
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {limitReached && (
                <p className="text-yellow-400 text-xs">
                  You've reached this week's image limit. You can still use the
                  thumbnail ideas to design your own.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ThumbnailsPanel;
